import {
  ApiError,
  apiErrorMessage,
  useJob,
  useSendJob,
  useStartSourcing,
  useUpdateJob,
} from "@vidario/api-client";
import { type Job, type JobCreateInput } from "@vidario/domain";
import { Breadcrumb, useToast } from "@vidario/ui";
import { useState, type ReactElement } from "react";
import { Navigate, useNavigate, useParams } from "react-router";

import { useIsAdmin } from "../../guards/useRole";
import { EMPTY_JOB, JobForm } from "../job-create/JobForm";
import { NO_SEARCHES_MESSAGE, SearchQuotaHint, useSearchQuota } from "../job-create/SearchQuota";
import { RestartSourcingDialog } from "../longlist/RestartSourcingDialog";

function toFormValues(job: Job): JobCreateInput {
  return {
    title: job.title,
    company: job.company,
    location: job.location,
    workModel: job.workModel,
    // Sent only when a place is picked again; the saved ones stay (#49).
    lat: null,
    lng: null,
    industry: job.industry,
    description: job.description,
    tasks: [...job.tasks],
    benefits: [...job.benefits],
    hiringPriorities: [...job.hiringPriorities],
    criteria: job.criteria.map((c) => ({
      name: c.name,
      importance: c.importance,
      required: c.required,
      description: c.description,
    })),
    seniority: job.seniority,
    employmentType: job.employmentType,
    salaryFrom: job.salaryFrom,
    salaryTo: job.salaryTo,
    targetStartDate: job.targetStartDate,
    notes: job.notes,
    sourceUrl: job.sourceUrl,
  };
}

/** Edit an existing Stelle. Drafts get the full create-style flow with
 * an "An Vidario senden" path (save, then start the search — #16);
 * already-running stellen
 * (kandidatensuche / longlist / outreaching / empfehlung) get a
 * single "Änderungen speichern" path. Terminal states (placed, closed)
 * bounce to the longlist. */
export function JobEditRoute(): ReactElement {
  const navigate = useNavigate();
  const toast = useToast();
  // The backend's reasons per field, shown next to the fields (#22).
  const [serverErrors, setServerErrors] = useState<Record<string, string[]>>({});
  const onSaveError = (err: Error): void => {
    setServerErrors(err instanceof ApiError ? err.fieldErrors : {});
    toast.push({ kind: "error", title: "Fehler beim Speichern", message: err.message });
  };
  const { jobId = "" } = useParams();
  const job = useJob(jobId);
  const updateJob = useUpdateJob();
  const startSourcing = useStartSourcing();
  const sendJob = useSendJob();
  const isAdmin = useIsAdmin();
  const { canStartSearch } = useSearchQuota();
  // Holds the validated edited values while the admin confirms the
  // destructive "save & restart" action; non-null drives the dialog open.
  const [restartValues, setRestartValues] = useState<JobCreateInput | null>(null);

  if (job.isLoading) {
    return (
      <main className="mx-auto max-w-[var(--container-max)] px-4 py-10 sm:px-8">
        <p className="text-sm text-text-secondary" role="status">
          Lade Stelle…
        </p>
      </main>
    );
  }

  if (job.isError || !job.data) {
    return <Navigate to="/dashboard" replace />;
  }

  // Terminal stellen are read-only.
  if (job.data.status === "placed" || job.data.status === "closed") {
    return <Navigate to={`/jobs/${jobId}/longlist`} replace />;
  }

  const isDraft = job.data.status === "draft";
  const mode = isDraft ? "edit-draft" : "edit-running";

  const save = (values: JobCreateInput): void => {
    updateJob.mutate(
      { id: jobId, input: values },
      {
        onSuccess: (updated) => {
          toast.push({
            kind: "success",
            title: isDraft ? "Entwurf aktualisiert" : "Änderungen gespeichert",
            message: `Änderungen an „${updated.title}" wurden gespeichert.`,
          });
          // For drafts we send back to the Dashboard; for running stellen
          // we land in the Longlist where the recruiter usually came from.
          void navigate(isDraft ? "/dashboard" : `/jobs/${jobId}/longlist`);
        },
        onError: onSaveError,
      },
    );
  };

  const send = (values: JobCreateInput): void => {
    if (!canStartSearch) {
      toast.push({ kind: "error", title: "Keine Suchen verfügbar", message: NO_SEARCHES_MESSAGE });
      return;
    }
    sendJob.mutate(
      { jobId, input: values },
      {
        onSuccess: (result) => {
          if (result.started) {
            toast.push({
              kind: "success",
              title: "An Vidario gesendet",
              message: `„${result.job.title}" ist live — die Kandidatensuche läuft.`,
            });
            void navigate(`/jobs/${jobId}/longlist`);
            return;
          }
          // The edits are saved and the job is still a Draft: stay here.
          toast.push({
            kind: "error",
            title: "Suche konnte nicht gestartet werden",
            message: `${apiErrorMessage(result.error)} Deine Änderungen wurden als Entwurf gespeichert.`,
          });
        },
        onError: onSaveError,
      },
    );
  };

  // Admin-only combo: save the edited parameters, then restart the search
  // with them. JobForm validates first and hands us the values; we open a
  // confirm (the restart drops all non-selected talents) before acting.
  const confirmSaveAndRestart = (): void => {
    const values = restartValues;
    if (!values) return;
    updateJob.mutate(
      { id: jobId, input: values },
      {
        onSuccess: () => {
          startSourcing.mutate(jobId, {
            onSuccess: () => {
              setRestartValues(null);
              toast.push({
                kind: "success",
                title: "Suche neu gestartet",
                message:
                  "Die Parameter wurden gespeichert und die Kandidatensuche läuft mit ihnen neu.",
              });
              void navigate(`/jobs/${jobId}/longlist`);
            },
            onError: (err) => {
              toast.push({
                kind: "error",
                title: "Suche konnte nicht gestartet werden",
                message: apiErrorMessage(err),
              });
            },
          });
        },
        onError: onSaveError,
      },
    );
  };

  return (
    <>
      <main className="mx-auto max-w-[var(--container-max)] px-4 py-10 sm:px-8 pb-20">
        <Breadcrumb
          className="mb-5"
          items={[
            { label: "Dashboard", onClick: () => void navigate("/dashboard") },
            { label: job.data.title },
          ]}
        />

        <div className="mb-8 flex items-end justify-between gap-6">
          <div>
            <h1 className="m-0 text-3xl font-bold -tracking-[0.5px]">
              {isDraft ? "Entwurf bearbeiten" : "Stelle bearbeiten"}
            </h1>
            <p className="mt-1.5 text-sm text-text-tertiary">
              {isDraft
                ? "Ergänze fehlende Felder und sende die Stelle an Vidario, sobald sie fertig ist."
                : isAdmin
                  ? "Aktualisiere die Stellendetails. Speichern lässt die laufende Suche unberührt; Speichern & Suche neu starten führt sie mit den neuen Parametern erneut aus und ersetzt dabei die nicht ausgewählten Talente."
                  : "Aktualisiere die Stellendetails. Die laufende Kandidatensuche bleibt davon unberührt."}
            </p>
            {isDraft && <SearchQuotaHint />}
          </div>
        </div>

        <JobForm
          mode={mode}
          initialValues={{ ...EMPTY_JOB, ...toFormValues(job.data) }}
          submitting={updateJob.isPending || startSourcing.isPending || sendJob.isPending}
          serverErrors={serverErrors}
          placeConfirmed={job.data.distanceAvailable}
          onCancel={() => void navigate(isDraft ? "/dashboard" : `/jobs/${jobId}/longlist`)}
          onSubmit={(values, submit) => (submit ? send(values) : save(values))}
          {...(isAdmin && mode === "edit-running"
            ? { onSaveAndRestart: (values: JobCreateInput) => setRestartValues(values) }
            : {})}
        />
      </main>
      <RestartSourcingDialog
        jobId={jobId}
        open={restartValues !== null}
        onOpenChange={(open) => {
          if (!open) setRestartValues(null);
        }}
        onConfirm={confirmSaveAndRestart}
        loading={updateJob.isPending || startSourcing.isPending}
      />
    </>
  );
}
